import moment from 'moment';
import equal from 'fast-deep-equal/react';
import get from 'lodash/get';
import React from 'react';
import styles from './StartBoosters.scss';

import type { IBooster, IReward } from '~/types';
import type { RootState } from '~/store';

import { setSelectedBooster } from '~/store/app';
import { buyBooster as buyBoosterAction } from '~/store/buy';
import { getStartBoostersIds, isTooLateToBuyStartBooster } from '~/utils/boosters';
import { isInIframe } from '~/utils';
import { useAppDispatch, useAppSelector } from '~/hooks';
import StartBundle from '~/Components/StartBundle/StartBundle';

export interface StartBoostersState {
    boughtBoostersIds: Array<string>,
    boosters: Array<IBooster>,
    premiumHiddenBooster: IBooster,
    gold: number,
    rewards: Array<IReward>,
    progress: number,
    showThirdCard: boolean,
    singleBoosterPrice: number,
    isLocked: boolean,
}

const stateSelector = (state: RootState): StartBoostersState => {
    const startBoostersIds = getStartBoostersIds(state.app.boosters);
    const boosters = state.app.boosters.filter((booster) => startBoostersIds.includes(booster.id)).reverse();
    const otherBoosters = state.app.boosters
        .filter((booster) => !startBoostersIds.includes(booster.id))
        .filter((booster) => booster.isPremium && !booster.isHidden);
    const singleBoosterPrice = otherBoosters[0] ? otherBoosters[0].price : 0;
    const premiumHiddenBooster = otherBoosters[0] || null;
    const gold = get(state.app, 'account.wallet.gold', 0) as number;
    const progress = get(state.app, 'account.progress', 0) as number;
    const { boughtBoostersIds } = state.app.account;
    const isStartBoosterBought = state.app.isStartBoostersBought;
    const now = moment.utc().unix() * 1000;
    const isProgressToSmall = !isTooLateToBuyStartBooster(state);
    const appIsReady = state.app.appIsReady && state.app.browserIsReady;
    const isEventFinished = now >= state.app.closeTime && appIsReady;
    const isLocked = isEventFinished || !isProgressToSmall || isStartBoosterBought;

    return {
        boughtBoostersIds: boughtBoostersIds || [],
        boosters,
        premiumHiddenBooster,
        gold,
        rewards: state.app.rewards,
        progress,
        showThirdCard: state.app.showThirdCard,
        singleBoosterPrice,
        isLocked,
    };
};

const StartBoosters = () => {
    const {
        boughtBoostersIds, boosters, premiumHiddenBooster, gold, rewards, progress, singleBoosterPrice, isLocked
    } = useAppSelector(
        stateSelector,
        equal,
    );

    const dispatch = useAppDispatch();
    const isFrame = isInIframe();

    const buyBooster = () => {
        dispatch(buyBoosterAction());
    };
    const handleSetSelectedBooster = (booster: IBooster) => {
        dispatch(setSelectedBooster(booster));
    };

    const onBuy = (booster: IBooster) => () => {
        handleSetSelectedBooster(booster);
        buyBooster();
    };

    const getRewards = (booster: IBooster): Array<IReward> => {
        const from = progress;
        const to = from + (booster.bonus.shipLevel || 0);
        return rewards.filter((_: IReward, i: number) => i >= from && i < to);
    };

    const renderBoosters = () => {
        if (boosters.length === 0) {
            return null;
        }

        const boostersArray: Array<React.ReactNode> = [];
        boosters.forEach((booster: IBooster, index: number) => {
            const notEnoughMoney = gold < booster.price;
            const needGold = booster.price - gold;
            const isBought = boughtBoostersIds.includes(booster.id);

            const filteredRewards = getRewards(booster);
            boostersArray.push(
                <StartBundle
                    title={booster.title}
                    description={booster.description}
                    key={`StartBundle-${index}-${booster.id}`}
                    isLocked={isLocked}
                    index={index}
                    count={booster.bonus.shipLevel || 0}
                    price={booster.price}
                    onBuy={onBuy(booster)}
                    isBought={isBought}
                    notEnoughMoney={notEnoughMoney}
                    needGold={needGold}
                    rewards={filteredRewards}
                    allBoostersCount={boosters.length}
                />,
            );
        });

        const notEnoughMoney = gold < singleBoosterPrice;
        const needGold = singleBoosterPrice - gold;

        if (!isFrame) {
            boostersArray.push(
                <StartBundle
                    title={premiumHiddenBooster.title}
                    description={premiumHiddenBooster.description}
                    index={boosters.length}
                    key={'StartBundle-by-one'}
                    count={1}
                    price={singleBoosterPrice}
                    isBought={false}
                    notEnoughMoney={notEnoughMoney}
                    needGold={needGold}
                    isLocked={true}
                    rewards={[]}
                />,
            );
        }
        return boostersArray;
    };

    return <div className={styles.bundles}>{renderBoosters()}</div>;
};

export default React.memo(StartBoosters);
